/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { parseVerilog, propagateLogic, parseYosysJson } from './astParser';
import { ZoomIn, ZoomOut, Maximize2, RotateCcw, Download, Info, Check } from 'react-feather';

// ── Zoom Constants ────────────────────────────────────────────────────────────
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 3.5;
const ZOOM_STEP = 0.15;

const SchematicViewer = ({
  theme,
  problemTitle,
  code,
  parsedGraph: parentGraph,
  logicValues: parentValues,
  onToggleInput: parentToggleInput
}) => {
  const [localGraph, setLocalGraph] = useState(null);
  const [localValues, setLocalValues] = useState({});

  // ── Pan & Zoom State ───────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(1);
  const [pan, setPan]   = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan]   = useState({ x: 0, y: 0 });

  // ── Interactive Net Tracing ────────────────────────────────────────────────
  const [hoveredNet, setHoveredNet]   = useState(null);
  const [selectedNet, setSelectedNet] = useState(null);

  const containerRef = useRef(null);

  // Self-parse code if parent didn't provide parsedGraph
  useEffect(() => {
    if (!parentGraph && code) {
      try {
        let graph;
        if (code.trim().startsWith('{')) {
          const jsonObj = JSON.parse(code);
          graph = parseYosysJson(jsonObj);
        } else {
          graph = parseVerilog(code);
        }

        if (graph) {
          setLocalGraph(graph);
          setLocalValues(prev => {
            const next = {};
            (graph.inputs || []).forEach(inp => {
              next[inp] = prev[inp] !== undefined ? prev[inp] : 0;
            });
            return propagateLogic(graph, next);
          });
        }
      } catch (err) {
        console.error("Schematic parse error:", err);
      }
    }
  }, [code, parentGraph]);

  const activeGraph = parentGraph || localGraph || { inputs: [], outputs: [], wires: [], gates: [] };
  const activeValues = parentValues || localValues;

  const inputsList  = useMemo(() => activeGraph.inputs  || [], [activeGraph]);
  const outputsList = useMemo(() => activeGraph.outputs || [], [activeGraph]);
  const gatesList   = useMemo(() => activeGraph.gates   || [], [activeGraph]);

  const activeHighlightedNet = hoveredNet || selectedNet;

  // ── Pan and Zoom Handlers ──────────────────────────────────────────────────
  const handleMouseDown = (e) => {
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    setIsPanning(true);
    setStartPan({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;
    setPan({
      x: e.clientX - startPan.x,
      y: e.clientY - startPan.y
    });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

  const handleZoomIn  = () => setZoom(z => clampZoom(+(z + ZOOM_STEP).toFixed(2)));
  const handleZoomOut = () => setZoom(z => clampZoom(+(z - ZOOM_STEP).toFixed(2)));

  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setSelectedNet(null);
  };

  const handleFit = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    setZoom(0.85);
    setPan({ x: 20, y: 10 });
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setZoom(z => clampZoom(+(z + delta).toFixed(2)));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ── Export SVG ─────────────────────────────────────────────────────────────
  const exportSvg = () => {
    const svgEl = containerRef.current?.querySelector('svg');
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgEl);
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schematic_${problemTitle || 'design'}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Layered Sugiyama Layout Engine ─────────────────────────────────────────
  const layout = useMemo(() => {
    if (inputsList.length === 0 && outputsList.length === 0 && gatesList.length === 0) {
      return null;
    }

    // 1. Assign topological levels
    const netLevels = {};
    inputsList.forEach(inp => { netLevels[inp] = 0; });

    const gateLevels = {};
    const unresolvedGates = [...gatesList];
    let resolvedAny = true;
    let iterations = 0;

    while (unresolvedGates.length > 0 && resolvedAny && iterations < 80) {
      resolvedAny = false;
      iterations++;
      for (let i = unresolvedGates.length - 1; i >= 0; i--) {
        const gate = unresolvedGates[i];
        const canResolve = gate.inputs.every(inp => netLevels[inp] !== undefined);
        if (canResolve) {
          const maxInLevel = gate.inputs.reduce((max, inp) => Math.max(max, netLevels[inp] || 0), 0);
          const lvl = maxInLevel + 1;
          gateLevels[gate.id] = lvl;
          gate.outputs.forEach(out => { netLevels[out] = lvl; });
          unresolvedGates.splice(i, 1);
          resolvedAny = true;
        }
      }
    }

    // Default unresolved or cycle gates (e.g. feedback loops in sequential logic)
    unresolvedGates.forEach((gate, idx) => {
      const lvl = gate.type.includes('DFF') ? 2 : 1;
      gateLevels[gate.id] = lvl;
      gate.outputs.forEach(out => { netLevels[out] = lvl; });
    });

    // 2. Group gates by stage column
    const columns = {};
    gatesList.forEach(g => {
      const col = gateLevels[g.id] || 1;
      if (!columns[col]) columns[col] = [];
      columns[col].push(g);
    });

    const maxCol = Math.max(1, ...Object.keys(columns).map(Number));

    // Spacing
    const COL_WIDTH = 220;
    const ROW_HEIGHT = 80;

    const inputCoords  = {};
    const outputCoords = {};
    const gateCoords   = {};

    // Position inputs on Column 0
    inputsList.forEach((inp, idx) => {
      inputCoords[inp] = { x: 50, y: 60 + idx * ROW_HEIGHT };
    });

    // Position gates by column
    Object.keys(columns).forEach(colStr => {
      const c = Number(colStr);
      const colGates = columns[c];
      colGates.forEach((g, rIdx) => {
        gateCoords[g.id] = {
          x: 100 + c * COL_WIDTH,
          y: 50 + rIdx * (ROW_HEIGHT + 24)
        };
      });
    });

    // Position outputs on the final column
    const outColX = 140 + (maxCol + 1) * COL_WIDTH;
    outputsList.forEach((out, idx) => {
      outputCoords[out] = { x: outColX, y: 60 + idx * ROW_HEIGHT };
    });

    const canvasW = Math.max(800, outColX + 160);
    const maxRows = Math.max(inputsList.length, outputsList.length, ...Object.values(columns).map(c => c.length));
    const canvasH = Math.max(450, maxRows * (ROW_HEIGHT + 24) + 100);

    return {
      inputCoords,
      outputCoords,
      gateCoords,
      canvasW,
      canvasH
    };
  }, [inputsList, outputsList, gatesList]);

  const isLight   = theme === 'light';
  const svgBg     = isLight ? '#f8fafc' : '#060a14';
  const gridLine  = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.03)';
  const borderCol = isLight ? '#cbd5e1' : '#1e293b';
  const textCol   = isLight ? '#0f172a' : '#f1f5f9';
  const mutedCol  = isLight ? '#64748b' : '#64748b';
  const headerBg  = isLight ? '#f1f5f9' : '#0b1020';

  if (!layout) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', height: '100%',
        backgroundColor: svgBg, color: textCol, alignItems: 'center',
        justifyContent: 'center', textAlign: 'center', gap: '12px', padding: '20px'
      }}>
        <span style={{ fontSize: '32px' }}>🔌</span>
        <strong style={{ fontSize: '13px' }}>Logic Schematic Not Generated</strong>
        <p style={{ margin: 0, fontSize: '11px', color: mutedCol, maxWidth: '340px', lineHeight: 1.6 }}>
          Run <strong>Yosys Logic Synthesis</strong> to generate and display the interactive digital schematic netlist.
        </p>
      </div>
    );
  }

  const { inputCoords, outputCoords, gateCoords, canvasW, canvasH } = layout;

  // Helper to locate output pin coordinates for any net
  const getNetSourceCoord = (netName) => {
    if (inputCoords[netName]) return { x: inputCoords[netName].x + 44, y: inputCoords[netName].y + 12 };
    const producer = gatesList.find(g => (g.outputs || []).includes(netName));
    if (producer && gateCoords[producer.id]) {
      const gX = gateCoords[producer.id].x;
      const gY = gateCoords[producer.id].y;
      return { x: gX + 64, y: gY + 28 };
    }
    return { x: 50, y: 50 };
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      backgroundColor: svgBg, color: textCol, userSelect: 'none', overflow: 'hidden'
    }}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '6px 14px', borderBottom: `1px solid ${borderCol}`,
        backgroundColor: headerBg, fontSize: '11px', flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#38bdf8', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🔌</span>
            <span>Synthesized Logic Schematic</span>
            {problemTitle && <span style={{ color: mutedCol, fontSize: '10px' }}>({problemTitle})</span>}
          </span>
          <span style={{ color: mutedCol, fontSize: '10px' }}>· Click nets to highlight · Drag to pan</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            onClick={handleZoomOut}
            title="Zoom Out"
            style={{
              padding: '3px 8px', background: isLight ? '#ffffff' : '#1e293b',
              border: `1px solid ${borderCol}`, color: textCol,
              borderRadius: '4px', cursor: 'pointer'
            }}
          >
            <ZoomOut size={12} />
          </button>

          <span style={{ color: mutedCol, fontSize: '10px', minWidth: '38px', textAlign: 'center' }}>
            {(zoom * 100).toFixed(0)}%
          </span>

          <button
            onClick={handleZoomIn}
            title="Zoom In"
            style={{
              padding: '3px 8px', background: isLight ? '#ffffff' : '#1e293b',
              border: `1px solid ${borderCol}`, color: textCol,
              borderRadius: '4px', cursor: 'pointer'
            }}
          >
            <ZoomIn size={12} />
          </button>

          <button
            onClick={handleFit}
            title="Fit to Screen"
            style={{
              padding: '3px 8px', background: isLight ? '#ffffff' : '#1e293b',
              border: `1px solid ${borderCol}`, color: textCol,
              borderRadius: '4px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'
            }}
          >
            <Maximize2 size={11} /> Fit
          </button>

          <button
            onClick={handleReset}
            title="Reset Pan & Zoom"
            style={{
              padding: '3px 8px', background: isLight ? '#ffffff' : '#1e293b',
              border: `1px solid ${borderCol}`, color: textCol,
              borderRadius: '4px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'
            }}
          >
            <RotateCcw size={11} /> 100%
          </button>

          <div style={{ width: 1, height: 16, background: borderCol, margin: '0 4px' }} />

          <button
            onClick={exportSvg}
            title="Export Schematic as SVG Image"
            style={{
              padding: '3px 8px', background: '#3b82f6',
              border: 'none', color: '#ffffff',
              borderRadius: '4px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'
            }}
          >
            <Download size={11} /> SVG
          </button>
        </div>
      </div>

      {/* ── Main Canvas Viewport ─────────────────────────────────────────── */}
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{
          flex: 1, overflow: 'hidden', position: 'relative',
          cursor: isPanning ? 'grabbing' : 'grab'
        }}
      >
        <svg
          width={canvasW}
          height={canvasH}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'top left',
            transition: isPanning ? 'none' : 'transform 0.05s ease-out'
          }}
        >
          <defs>
            <marker id="wire-arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 2 L 7 5 L 0 8 z" fill="#64748b" />
            </marker>
            <marker id="wire-arrow-active" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 2 L 7 5 L 0 8 z" fill="#38bdf8" />
            </marker>
          </defs>

          {/* Background Grid Pattern */}
          <pattern id="schematic-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <line x1="0" y1="0" x2="40" y2="0" stroke={gridLine} strokeWidth="1" />
            <line x1="0" y1="0" x2="0" y2="40" stroke={gridLine} strokeWidth="1" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#schematic-grid)" />

          {/* ================= WIRES (Routed with Channel Offsets) ================= */}
          {gatesList.map(gate => {
            const gatePos = gateCoords[gate.id];
            if (!gatePos) return null;

            return (gate.inputs || []).map((inp, inpIdx) => {
              const src = getNetSourceCoord(inp);
              const destX = gatePos.x;
              const destY = gatePos.y + (gate.inputs.length === 1 ? 28 : (inpIdx === 0 ? 18 : 38));

              const isHighlighted = activeHighlightedNet === inp;
              const hasActiveSelection = activeHighlightedNet !== null;

              // Channel routing offset to prevent wire overlap
              const channelOffset = 24 + ((gate.id * 7 + inpIdx * 11) % 6) * 12;
              const midX = src.x + Math.max(channelOffset, (destX - src.x) * 0.45);
              const pathD = `M ${src.x},${src.y} L ${midX},${src.y} L ${midX},${destY} L ${destX},${destY}`;

              return (
                <g key={`${gate.id}-wire-${inpIdx}`}>
                  {/* Invisible thicker hit-box path for easier clicking */}
                  <path
                    d={pathD}
                    stroke="transparent"
                    strokeWidth="12"
                    fill="none"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHoveredNet(inp)}
                    onMouseLeave={() => setHoveredNet(null)}
                    onClick={(e) => { e.stopPropagation(); setSelectedNet(selectedNet === inp ? null : inp); }}
                  />
                  {/* Visible wire path */}
                  <path
                    d={pathD}
                    stroke={isHighlighted ? '#38bdf8' : (hasActiveSelection ? '#1e293b' : '#475569')}
                    strokeWidth={isHighlighted ? '2.5' : '1.5'}
                    fill="none"
                    strokeDasharray={inp.includes('clk') ? '4 3' : 'none'}
                    markerEnd={isHighlighted ? 'url(#wire-arrow-active)' : 'url(#wire-arrow)'}
                    style={{ transition: 'stroke 0.15s, stroke-width 0.15s' }}
                  />
                </g>
              );
            });
          })}

          {/* Primary Output Wires */}
          {outputsList.map((out, outIdx) => {
            const src = getNetSourceCoord(out);
            const dest = outputCoords[out];
            if (!src || !dest) return null;

            const isHighlighted = activeHighlightedNet === out;
            const hasActiveSelection = activeHighlightedNet !== null;
            const midX = src.x + (dest.x - src.x) * 0.5;
            const pathD = `M ${src.x},${src.y} L ${midX},${src.y} L ${midX},${dest.y + 12} L ${dest.x},${dest.y + 12}`;

            return (
              <g key={`out-wire-${outIdx}`}>
                <path
                  d={pathD}
                  stroke="transparent"
                  strokeWidth="12"
                  fill="none"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredNet(out)}
                  onMouseLeave={() => setHoveredNet(null)}
                  onClick={(e) => { e.stopPropagation(); setSelectedNet(selectedNet === out ? null : out); }}
                />
                <path
                  d={pathD}
                  stroke={isHighlighted ? '#38bdf8' : (hasActiveSelection ? '#1e293b' : '#10b981')}
                  strokeWidth={isHighlighted ? '2.5' : '1.5'}
                  fill="none"
                  markerEnd={isHighlighted ? 'url(#wire-arrow-active)' : 'url(#wire-arrow)'}
                />
              </g>
            );
          })}

          {/* ================= GATES & FUNCTIONAL CELLS ================= */}
          {gatesList.map(gate => {
            const pos = gateCoords[gate.id];
            if (!pos) return null;

            const isDFF = gate.type.includes('DFF') || gate.type.includes('dff');
            const isMUX = gate.type.includes('MUX') || gate.type.includes('mux');
            const isADD = gate.type.includes('ADD') || gate.type.includes('SUB');
            const isAND = gate.type.includes('AND');
            const isOR  = gate.type.includes('OR');
            const isXOR = gate.type.includes('XOR');
            const isNOT = gate.type.includes('NOT') || gate.type.includes('INV');

            const hasActiveNet = (gate.inputs || []).some(i => i === activeHighlightedNet) ||
                                 (gate.outputs || []).some(o => o === activeHighlightedNet);

            if (isDFF) {
              // ── Register Cell (D-Flip-Flop) ───────────────────────────
              return (
                <g key={gate.id} transform={`translate(${pos.x}, ${pos.y})`}>
                  <rect
                    width="64" height="56" rx="4"
                    fill={isLight ? '#ffffff' : '#0f172a'}
                    stroke={hasActiveNet ? '#38bdf8' : '#6366f1'}
                    strokeWidth={hasActiveNet ? '2.5' : '2'}
                  />
                  {/* Clock trigger triangle */}
                  <polygon points="0,32 8,38 0,44" fill="none" stroke="#6366f1" strokeWidth="1.5" />
                  <text x="12" y="41" fontSize="9" fill="#818cf8" fontWeight="bold">CLK</text>
                  <text x="6" y="22" fontSize="9" fill="#818cf8" fontWeight="bold">D</text>
                  <text x="50" y="32" fontSize="9" fill="#10b981" fontWeight="bold">Q</text>
                  <text x="32" y="16" fontSize="8" textAnchor="middle" fill={mutedCol} fontWeight="bold">DFF</text>
                  <text x="32" y="50" fontSize="7" textAnchor="middle" fill={mutedCol}>
                    {gate.name}
                  </text>
                </g>
              );
            }

            if (isMUX) {
              // ── Multiplexer Block ─────────────────────────────────────
              return (
                <g key={gate.id} transform={`translate(${pos.x}, ${pos.y})`}>
                  <polygon
                    points="0,6 50,14 50,42 0,50"
                    fill={isLight ? '#ffffff' : '#0f172a'}
                    stroke={hasActiveNet ? '#38bdf8' : '#f59e0b'}
                    strokeWidth={hasActiveNet ? '2.5' : '2'}
                  />
                  <text x="8" y="22" fontSize="8" fill="#f59e0b" fontWeight="bold">0</text>
                  <text x="8" y="38" fontSize="8" fill="#f59e0b" fontWeight="bold">1</text>
                  <text x="40" y="31" fontSize="8" fill="#10b981" fontWeight="bold">Y</text>
                  <text x="25" y="12" fontSize="7" textAnchor="middle" fill={mutedCol}>MUX</text>
                </g>
              );
            }

            if (isADD) {
              // ── Arithmetic Block (Adder / Subtractor) ─────────────────
              return (
                <g key={gate.id} transform={`translate(${pos.x}, ${pos.y})`}>
                  <rect
                    width="56" height="52" rx="4"
                    fill={isLight ? '#ffffff' : '#0f172a'}
                    stroke={hasActiveNet ? '#38bdf8' : '#10b981'}
                    strokeWidth={hasActiveNet ? '2.5' : '2'}
                  />
                  <text x="28" y="30" fontSize="16" textAnchor="middle" fill="#10b981" fontWeight="bold">+</text>
                  <text x="8" y="20" fontSize="8" fill="#64748b">A</text>
                  <text x="8" y="40" fontSize="8" fill="#64748b">B</text>
                  <text x="42" y="30" fontSize="8" fill="#10b981" fontWeight="bold">Y</text>
                </g>
              );
            }

            // ── Standard Logic Gate (AND, OR, XOR, NOT) ───────────────
            let strokeColor = '#3b82f6';
            let label = 'AND';
            let gatePath = "M 0,6 L 24,6 C 44,6 44,50 24,50 L 0,50 Z";

            if (isXOR) {
              strokeColor = '#a855f7';
              label = 'XOR';
              gatePath = "M 0,6 C 12,14 12,42 0,50 C 14,50 28,44 42,28 C 28,12 14,6 0,6 Z";
            } else if (isOR) {
              strokeColor = '#ef4444';
              label = 'OR';
              gatePath = "M 0,6 C 10,14 10,42 0,50 C 14,50 28,44 42,28 C 28,12 14,6 0,6 Z";
            } else if (isNOT) {
              strokeColor = '#f59e0b';
              label = 'INV';
              gatePath = "M 0,14 L 32,28 L 0,42 Z";
            }

            return (
              <g key={gate.id} transform={`translate(${pos.x}, ${pos.y})`}>
                <path
                  d={gatePath}
                  fill={isLight ? '#ffffff' : '#0f172a'}
                  stroke={hasActiveNet ? '#38bdf8' : strokeColor}
                  strokeWidth={hasActiveNet ? '2.5' : '2'}
                />
                {isNOT && (
                  <circle cx="36" cy="28" r="3.5" fill={svgBg} stroke={strokeColor} strokeWidth="1.5" />
                )}
                <text x="14" y="32" fill={strokeColor} fontSize="8" fontWeight="bold">{label}</text>
              </g>
            );
          })}

          {/* ================= PRIMARY INPUT PORTS ================= */}
          {inputsList.map((inp) => {
            const pos = inputCoords[inp];
            if (!pos) return null;
            const isHighlighted = activeHighlightedNet === inp;

            return (
              <g
                key={`inp-port-${inp}`}
                transform={`translate(${pos.x - 20}, ${pos.y})`}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredNet(inp)}
                onMouseLeave={() => setHoveredNet(null)}
                onClick={() => setSelectedNet(selectedNet === inp ? null : inp)}
              >
                <rect
                  width="60" height="24" rx="4"
                  fill={isHighlighted ? 'rgba(56, 189, 248, 0.2)' : (isLight ? '#ffffff' : '#1e293b')}
                  stroke={isHighlighted ? '#38bdf8' : '#3b82f6'}
                  strokeWidth="1.5"
                />
                <text x="30" y="15" textAnchor="middle" fill={textCol} fontSize="9" fontWeight="bold">
                  {inp}
                </text>
              </g>
            );
          })}

          {/* ================= PRIMARY OUTPUT PORTS ================= */}
          {outputsList.map((out) => {
            const pos = outputCoords[out];
            if (!pos) return null;
            const isHighlighted = activeHighlightedNet === out;

            return (
              <g
                key={`out-port-${out}`}
                transform={`translate(${pos.x}, ${pos.y})`}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredNet(out)}
                onMouseLeave={() => setHoveredNet(null)}
                onClick={() => setSelectedNet(selectedNet === out ? null : out)}
              >
                <polygon
                  points="0,0 54,0 66,12 54,24 0,24"
                  fill={isHighlighted ? 'rgba(56, 189, 248, 0.2)' : (isLight ? '#ffffff' : '#1e293b')}
                  stroke={isHighlighted ? '#38bdf8' : '#10b981'}
                  strokeWidth="1.5"
                />
                <text x="28" y="15" textAnchor="middle" fill={textCol} fontSize="9" fontWeight="bold">
                  {out}
                </text>
              </g>
            );
          })}
        </svg>

        {/* ── Active Net Info Bar Overlay ─────────────────────────────────── */}
        {activeHighlightedNet && (
          <div style={{
            position: 'absolute', bottom: '12px', left: '16px',
            backgroundColor: isLight ? 'rgba(255,255,255,0.95)' : 'rgba(15, 23, 42, 0.95)',
            border: '1px solid #38bdf8', borderRadius: '4px',
            padding: '4px 10px', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)', zIndex: 10
          }}>
            <span style={{ color: '#38bdf8', fontWeight: 'bold' }}>Highlighted Net:</span>
            <span style={{ color: textCol, fontFamily: 'monospace' }}>{activeHighlightedNet}</span>
            <button
              onClick={() => { setSelectedNet(null); setHoveredNet(null); }}
              style={{ background: 'transparent', border: 'none', color: mutedCol, cursor: 'pointer', fontSize: '10px' }}
            >
              Clear
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchematicViewer;
